import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select, {
  MenuListProps,
  MenuPlacement,
  OptionProps,
  SingleValueProps,
  StylesConfig,
  components,
} from 'react-select';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { findUniqueHighlightBadges, HIGHLIGHTS_SIZE } from 'features/catalog/badge-hash';
import { gradientFromHighlightBadges } from 'features/catalog/badge-hash-gradient';
import { formatLastUpdated, getRankDisplay } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import {
  getProfileSearchSort,
  setProfileSearchSort,
  subscribeProfileSearchSort,
  type ProfileSearchSort,
} from 'features/profiles/profile-search-sort';
import { computeJourneyRarityCounts, computeRarityCounts, mergeRarityCounts } from 'features/catalog/rarity';
import { RankPrefix } from 'shared/components/RankPrefix';
import { RarityCountsDisplay } from 'shared/components/RarityCountsDisplay';
import '../explore/ExploreProfilesView.css';

type ProfileSelectExtras = {
  badgesMap: Map<string, Badge>;
  suppressOptionFocus: boolean;
};

const ProfileSelectExtrasContext = createContext<ProfileSelectExtras | null>(null);

const useProfileSelectExtras = (): ProfileSelectExtras => {
  const extras = useContext(ProfileSelectExtrasContext);
  if (!extras) throw new Error('Profile select extras missing');
  return extras;
};

export interface ProfileSearchOption {
  value: string;
  label: string;
  score: number;
  lastUpdated: Date;
  topEmoji: string;
  rank: number;
  profile: Profile;
}

interface ProfileSearchSelectProps {
  selectedProfileId?: string | null;
  onSelectProfile: (profileId: string) => void;
  onClear?: () => void;
  /** Keep the menu open (Explore page). */
  defaultMenuOpen?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  menuPlacement?: MenuPlacement;
}

const hashGradientForProfile = (profile: Profile, badgesMap: Map<string, Badge>): string | null => {
  const claimed = profile.badgesIDs.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => badge != null);
  if (claimed.length < HIGHLIGHTS_SIZE) return null;
  const hashBadges = findUniqueHighlightBadges(claimed, getFirstPartOfEmail(profile.email));
  if (!hashBadges || hashBadges.length !== HIGHLIGHTS_SIZE) return null;
  // Same wash as modal "Claimed by…" participant cards (white card).
  return gradientFromHighlightBadges(hashBadges, { surface: 'light' });
};

const OptionRow: React.FC<{
  option: ProfileSearchOption;
  badgesMap: Map<string, Badge>;
  compact?: boolean;
}> = ({ option, badgesMap, compact = false }) => {
  const rarityCounts = mergeRarityCounts(
    computeRarityCounts(option.profile.badgesIDs, badgesMap),
    computeJourneyRarityCounts(option.profile.badgesIDs)
  );

  return (
    <div className={`explore-option ${compact ? 'explore-option--compact' : ''}`}>
      <div className="explore-option-top">
        <span className="explore-option-identity">
          <RankPrefix rank={option.rank} /> {option.profile.name}
        </span>
        {!compact ? (
          <span className="explore-option-score">
            <i>{option.score}</i>
            {option.topEmoji ? <span className="explore-option-emoji">{option.topEmoji}</span> : null}
          </span>
        ) : null}
      </div>
      {!compact ? (
        <div className="explore-option-bottom">
          <span className="explore-option-updated">last updated: {formatLastUpdated(option.lastUpdated)}</span>
          <span className="explore-option-rarities">
            <RarityCountsDisplay
              counts={rarityCounts}
              itemClassName="rarity-count-dropdown-item"
              iconStyle={{ width: '14px', height: '14px' }}
            />
          </span>
        </div>
      ) : null}
    </div>
  );
};

/** Portal + react-select's offsetTop math disagree; use viewport rects instead. */
const scrollSelectedIntoView = (list: HTMLElement) => {
  const selected = list.querySelector<HTMLElement>('.explore-select__option--is-selected');
  if (!selected) return;

  const listRect = list.getBoundingClientRect();
  // Prefer aligning the profile 2 below selected with the bottom of the menu.
  const twoBelow = selected.nextElementSibling?.nextElementSibling;
  if (twoBelow instanceof HTMLElement) {
    const delta = twoBelow.getBoundingClientRect().bottom - listRect.bottom;
    if (Math.abs(delta) > 1) list.scrollTop += delta;
    return;
  }

  const last = list.querySelector<HTMLElement>('.explore-select__option:last-child');
  if (!last) return;
  const delta = last.getBoundingClientRect().bottom - listRect.bottom;
  if (Math.abs(delta) > 1) list.scrollTop += delta;
};

/** One-shot open scroll to the selected profile (2 rows above the menu bottom). */
const MenuListScrollToSelected = (props: MenuListProps<ProfileSearchOption>) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const propsInnerRefRef = useRef(props.innerRef);
  propsInnerRefRef.current = props.innerRef;

  const setListRef = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    const propsInnerRef = propsInnerRefRef.current;
    if (typeof propsInnerRef === 'function') propsInnerRef(node);
    else if (propsInnerRef) Object.assign(propsInnerRef, { current: node });
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    scrollSelectedIntoView(list);
  }, []);

  return <components.MenuList {...props} innerRef={setListRef} />;
};

/**
 * Stable Option — do NOT update focusedOption on mouse hover.
 * Hover focus setState retriggers react-select scrollIntoView (jump under the cursor).
 * Visual hover is CSS-only; keyboard still uses --is-focused.
 */
const ProfileSelectOption = (props: OptionProps<ProfileSearchOption>) => {
  const { badgesMap, suppressOptionFocus } = useProfileSelectExtras();
  const { onMouseMove: _onMouseMove, onMouseOver: _onMouseOver, ...innerProps } = props.innerProps;
  // Hash wash on hover + selected — selected matches modal Claimed-by strength.
  const hashGradient = hashGradientForProfile(props.data.profile, badgesMap);
  const isFocused = props.isFocused && !suppressOptionFocus;

  return (
    <components.Option
      {...props}
      isFocused={isFocused}
      className={[props.className, hashGradient ? 'explore-select__option--hash' : null].filter(Boolean).join(' ')}
      innerProps={{
        ...innerProps,
        style: {
          ...innerProps.style,
          ...(hashGradient ? { '--explore-option-hash': hashGradient } : null),
        },
      }}
    >
      <OptionRow option={props.data} badgesMap={badgesMap} />
    </components.Option>
  );
};

const ProfileSelectSingleValue = (props: SingleValueProps<ProfileSearchOption>) => {
  const badgesMap = useProfileSelectExtras().badgesMap;
  return (
    <components.SingleValue {...props}>
      <OptionRow option={props.data} badgesMap={badgesMap} compact />
    </components.SingleValue>
  );
};

const PROFILE_SELECT_COMPONENTS = {
  MenuList: MenuListScrollToSelected,
  Option: ProfileSelectOption,
  SingleValue: ProfileSelectSingleValue,
};

const MENU_PORTAL_STYLES: StylesConfig<ProfileSearchOption, false> = {
  menuPortal: (base) => ({ ...base, zIndex: 40 }),
};

export const buildProfileSearchOptions = (
  rankedProfiles: Profile[],
  sortBy: ProfileSearchSort = 'score'
): ProfileSearchOption[] => {
  const rankById = new Map(rankedProfiles.map((p, i) => [getFirstPartOfEmail(p.email), i + 1]));

  const opts: ProfileSearchOption[] = rankedProfiles.map((profile) => {
    const value = getFirstPartOfEmail(profile.email);
    return {
      value,
      label: `${getRankDisplay(rankById.get(value) ?? 0)} ${profile.name}`,
      score: profile.sortScore,
      lastUpdated: profile.lastActivityAt,
      topEmoji: profile.journeyEmojis.at(0) ?? '',
      rank: rankById.get(value) ?? 0,
      profile,
    };
  });

  if (sortBy === 'last updated') {
    return [...opts].sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime());
  }
  return opts;
};

export const ProfileSearchSelect: React.FC<ProfileSearchSelectProps> = ({
  selectedProfileId = null,
  onSelectProfile,
  onClear,
  defaultMenuOpen = false,
  autoFocus = false,
  placeholder = 'Select Profile',
  className,
  menuPlacement = 'auto',
}) => {
  const { rankedProfiles, badgesMap } = useTalosData();
  const [menuOpen, setMenuOpen] = useState(defaultMenuOpen);
  const [inputValue, setInputValue] = useState('');
  // react-select always focuses option[0] on open; suppress until search or keyboard nav.
  const [optionFocusEnabled, setOptionFocusEnabled] = useState(false);
  const [sortBy, setSortBy] = useState<ProfileSearchSort>(() => getProfileSearchSort());

  useEffect(() => subscribeProfileSearchSort(() => setSortBy(getProfileSearchSort())), []);

  const options = useMemo(() => buildProfileSearchOptions(rankedProfiles, sortBy), [rankedProfiles, sortBy]);
  // Always the same object reference as in `options` (react-select openMenu uses indexOf).
  const selected = useMemo(
    () => options.find((o) => o.value === selectedProfileId) ?? null,
    [options, selectedProfileId]
  );
  const suppressOptionFocus = !optionFocusEnabled && selected == null;

  const filterOption = useCallback((option: { data: ProfileSearchOption }, raw: string) => {
    const q = raw.trim().toLowerCase();
    if (!q) return true;
    const p = option.data.profile;
    return (
      p.name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      option.data.label.toLowerCase().includes(q) ||
      String(option.data.rank).includes(q) ||
      String(option.data.score).includes(q)
    );
  }, []);

  return (
    <div className={`explore-search ${className ?? ''}`.trim()}>
      <button
        type="button"
        className="explore-search-sort"
        title="Sort profiles"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setProfileSearchSort(sortBy === 'score' ? 'last updated' : 'score');
        }}
      >
        ↓ {sortBy}
      </button>
      <ProfileSelectExtrasContext.Provider value={{ badgesMap, suppressOptionFocus }}>
        <Select<ProfileSearchOption>
          classNamePrefix="explore-select"
          options={options}
          value={selected}
          getOptionValue={(o) => o.value}
          placeholder={placeholder}
          isClearable
          autoFocus={autoFocus}
          inputValue={inputValue}
          onInputChange={(next, meta) => {
            if (meta.action === 'input-change') {
              setInputValue(next);
              if (next.trim()) setOptionFocusEnabled(true);
            }
            if (meta.action === 'menu-close' || meta.action === 'set-value') setInputValue('');
          }}
          menuIsOpen={menuOpen}
          onMenuOpen={() => {
            setInputValue('');
            setMenuOpen(true);
          }}
          onMenuClose={() => {
            setInputValue('');
            setOptionFocusEnabled(false);
            setMenuOpen(false);
          }}
          onKeyDown={(e) => {
            const navKey =
              e.key === 'ArrowDown' ||
              e.key === 'ArrowUp' ||
              e.key === 'PageDown' ||
              e.key === 'PageUp' ||
              e.key === 'Home' ||
              e.key === 'End';
            if (navKey) {
              if (suppressOptionFocus && menuOpen) {
                // Reveal the already-focused option; don't advance on this first keypress.
                setOptionFocusEnabled(true);
                e.preventDefault();
                return;
              }
              setOptionFocusEnabled(true);
              return;
            }
            if (suppressOptionFocus && (e.key === 'Enter' || (e.key === ' ' && menuOpen))) {
              e.preventDefault();
            }
          }}
          tabSelectsValue={!suppressOptionFocus}
          // Hide the selected chip while open so the field shows placeholder + empty input.
          controlShouldRenderValue={!menuOpen}
          openMenuOnFocus={defaultMenuOpen}
          menuPlacement={menuPlacement}
          menuShouldScrollIntoView={false}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          styles={MENU_PORTAL_STYLES}
          filterOption={filterOption}
          onChange={(option) => {
            if (option?.value) onSelectProfile(option.value);
            else onClear?.();
          }}
          components={PROFILE_SELECT_COMPONENTS}
        />
      </ProfileSelectExtrasContext.Provider>
    </div>
  );
};
